import { useEffect, useState } from 'react';
import { useGetOrderList } from './useGetOrderList';
import { OrderDetail } from '~types/api/event';

/**
 * Custom hook to get upcoming events within the next 24 hours
 * @returns An object containing the upcoming event data, loading state, and error state
 */
export const useUpcomingEvents = () => {
  const [upcomingEvent, setUpcomingEvent] = useState<OrderDetail | null>(null);
  const { data: orders, isLoading, error } = useGetOrderList();

  useEffect(() => {
    if (orders && orders.length > 0) {
      // Find orders with events starting within the next 24 hours
      const now = new Date();
      const twentyFourHoursLater = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours
      const upcomingOrders = orders.filter(order => {
        // Only include successful orders
        if (order.status !== 'succeeded') return false;

        const validTypes = ['THE_REST_TABLE', 'CULTURAL_EDIT', 'HOUSEWARMING'];
        if (!validTypes.includes(order.event.event_service_type)) {
          return false;
        }

        // Check if the event has a start_date
        if (!order.event || !order.event.start_date) return false;

        // Parse the start date
        const eventStartDate = new Date(order.event.start_date);

        // Check if the event is in the next 24 hours
        return eventStartDate > now && eventStartDate <= twentyFourHoursLater;
      });

      // Get the closest upcoming event
      if (upcomingOrders.length > 0) {
        // Sort by start date (ascending)
        upcomingOrders.sort((a, b) => {
          const dateA = new Date(a.event.start_date).getTime();
          const dateB = new Date(b.event.start_date).getTime();
          return dateA - dateB;
        });

        // Set the closest upcoming event
        setUpcomingEvent(upcomingOrders[0]);
      } else {
        setUpcomingEvent(null);
      }
    }
  }, [orders]);

  return { upcomingEvent, isLoading, error };
};